import React, { useState } from "react";
import axios from "axios";
import {
  IconButton,
  Tooltip,
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Chip,
  Stack,
  Divider,
  useTheme,
  styled,
  CircularProgress
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close"; // Added this import
import { useDispatch } from "react-redux";
import { editSubscription } from "../../../Reducer/Subscription/subscriptionPlanSlice";
import { blue } from "@mui/material/colors";

const StyledModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ModalContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: 500,
  maxWidth: '90vw',
  maxHeight: '90vh',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[24],
  padding: theme.spacing(4),
  overflowY: 'auto',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    width: '95vw'
  }
}));

const StatusSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: theme.palette.success.main,
    '& + .MuiSwitch-track': {
      backgroundColor: theme.palette.success.main,
    },
  },
}));

const FeatureChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  '& .MuiChip-deleteIcon': {
    color: theme.palette.error.main,
    '&:hover': {
      color: theme.palette.error.dark,
    }
  }
}));

const EditSubscriptionButton = ({ plan }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(plan.title || "");
  const [duration, setDuration] = useState(plan.duration_months || "");
  const [price, setPrice] = useState(plan.price_usd || "");
  const [status, setStatus] = useState(
    plan.status === "Active" || plan.is_active === true
  );
  const [features, setFeatures] = useState(plan.features || []);
  const [newFeature, setNewFeature] = useState("");

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const [originalStatus] = useState(
    plan.status === "Active" || plan.is_active === true
  );

  const handleOpen = () => {
    setOpen(true);
    setSuccess("");
    setError("");
    setTitle(plan.title || "");
    setDuration(plan.duration_months || "");
    setPrice(plan.price_usd || "");
    setStatus(plan.status === "Active" || plan.is_active === true);
    setFeatures(plan.features || []);
    setNewFeature("");
  };

  const handleClose = () => {
    setOpen(false);
    setSuccess("");
    setError("");
    setNewFeature("");
  };

  const handleAddFeature = () => {
    if (newFeature.trim() && !features.includes(newFeature.trim())) {
      setFeatures([...features, newFeature.trim()]);
      setNewFeature("");
    }
  };

  const handleRemoveFeature = (featureToRemove) => {
    setFeatures(features.filter(feature => feature !== featureToRemove));
  };

  const handleFeatureKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddFeature();
    }
  };

  const handleSave = async () => {
    if (!title || !duration || !price) {
      setError("Title, duration, and price are required.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    const updatedPlan = {
      id: plan.id,
      title,
      duration_months: Number(duration),
      price_usd: Number(price),
      status: status ? "Active" : "Inactive",
      is_active: status,
      features: features,
    };

    const statusChanged = originalStatus !== status;

    try {
      const response = await axios.put(`/api/v1/subscription-plan/update-plan/${plan.id}`, updatedPlan);

      if (response.status === 200) {
        const data = response.data;
        
        const transformed = {
          ...plan,
          title: data.title || title,
          description: data.description || plan.description || "",
          duration_months: data.duration_months || Number(duration),
          price_usd: data.price_usd || Number(price),
          is_active: status,
          status: status ? "Active" : "Inactive",
          features: data.features || features,
          updatedAt: data.updatedAt || new Date().toISOString(),
        };

        setSuccess("Subscription updated successfully!");
        dispatch(editSubscription(transformed));

        setTimeout(() => {
          setSuccess("");
          handleClose();
          
          if (statusChanged) {
            window.location.reload();
          }
        }, 1000);
      } else {
        const errorText = response.statusText;
        if (
          errorText.includes("duplicate key value") &&
          errorText.includes("pricing_plans_title_key")
        ) {
          setError("A plan with this title already exists. Please choose a different name.");
        } else {
          setError(`Failed to update: ${errorText}`);
        }
      }
    } catch (error) {
      setError("Error: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Edit Subscription" arrow>
        <IconButton 
          color="primary" 
          onClick={handleOpen}
          sx={{
            '&:hover': {
              backgroundColor: theme.palette.primary.light,
              color: "#1976d2"
            }
          }}
        >
          <EditIcon />
        </IconButton>
      </Tooltip>

      <StyledModal open={open} onClose={handleClose}>
        <ModalContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" fontWeight="600">
              Edit Subscription Plan
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon /> {/* Changed from Close to CloseIcon */}
            </IconButton>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <TextField
            fullWidth
            label="Plan Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            margin="normal"
            variant="outlined"
            sx={{ mb: 2 }}
          />

          <Box display="flex" gap={2} mb={2}>
            <TextField
              fullWidth
              label="Duration (months)"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              margin="normal"
              type="number"
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Price (USD)"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              margin="normal"
              type="number"
              variant="outlined"
            />
          </Box>

          {/* Features Section */}
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
              Plan Features
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Add new feature..."
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                onKeyPress={handleFeatureKeyPress}
              />
              <Button
  variant="contained"
  size="small"
  onClick={handleAddFeature}
  disabled={!newFeature.trim() || features.includes(newFeature.trim())}
  startIcon={<AddIcon />}
  sx={{
    whiteSpace: 'nowrap',
    textTransform: 'none',
    fontWeight: 600,
    px: 3,
    py: 1,
    borderRadius: 2,
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
    '&:hover': {
      boxShadow: '0 4px 10px rgba(0,0,0,0.15)'
    }
  }}
>
  Add Feature
</Button>

            </Box>

            {features.length > 0 ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {features.map((feature, index) => (
                  <FeatureChip
                    key={index}
                    label={feature}
                    onDelete={() => handleRemoveFeature(feature)}
                    deleteIcon={<DeleteIcon fontSize="small" />}
                    variant="outlined"
                  />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No features added yet
              </Typography>
            )}
          </Box>

          <FormControlLabel
            control={
              <StatusSwitch
                checked={status}
                onChange={(e) => setStatus(e.target.checked)}
                disabled={loading}
              />
            }
            label={
              <Typography variant="body1">
                Status: <span style={{ 
                  color: status ? theme.palette.success.main : theme.palette.error.main,
                  fontWeight: 600
                }}>
                  {status ? "Active" : "Inactive"}
                </span>
              </Typography>
            }
            sx={{ mt: 2, mb: 3 }}
          />

          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 1, mb: 2 }}>
              {error}
            </Typography>
          )}
          {success && (
            <Typography color="success.main" variant="body2" sx={{ mt: 1, mb: 2 }}>
              {success}
            </Typography>
          )}

          <Divider sx={{ mt: 2, mb: 3 }} />

          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button 
              onClick={handleClose} 
              variant="outlined" 
              disabled={loading}
              sx={{
                borderRadius: theme.shape.borderRadius,
                px: 3,
                textTransform: 'none'
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              variant="contained" 
              color="primary" 
              disabled={loading}
              sx={{
                borderRadius: theme.shape.borderRadius,
                px: 3,
                textTransform: 'none'
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Saving...
                </>
              ) : 'Save Changes'}
            </Button>
          </Box>
        </ModalContent>
      </StyledModal>
    </>
  );
};

export default EditSubscriptionButton;